/**
 * O que a tela diz quando o endereço não abriu.
 *
 * A casa faz questão de que "manda o link desse atendimento" funcione — e o
 * link que chega tem tudo para não abrir: caso apagado, arquivado, de outro
 * escritório, sessão caída no caminho, servidor mudo. Todos davam a MESMA tela,
 * "Carregando…" para sempre, que é o pior recado possível: quem recebeu o link
 * fica olhando uma tela que nunca vai mudar sem saber que não vai.
 *
 * Aqui se decide, num lugar só, QUAL das cinco coisas aconteceu e qual saída
 * oferecer. É conta e não pintura (`ausencia.test.jsx` prende), e é a mesma
 * conta para atendimento, reunião, peça, pessoa e fluxo.
 *
 * A frase do servidor tem preferência quando existe: `{erro}` das rotas já vem
 * em português e é mais específico que qualquer coisa que a tela adivinhe
 * ("Atendimento não encontrado" é dele). O `daCasa` de `api.js` é o que separa
 * essa frase do texto cru do Hono, que não serve para ninguém ler.
 */

/*
 * Só o que muda a frase: o título pronto (português concorda em gênero, e
 * "Reunião não encontrado" faz a tela parecer de outro escritório) e as duas
 * formas usadas no meio do texto.
 */
const ALVOS = {
  atendimento: ["Atendimento não encontrado", "este atendimento", "o atendimento"],
  reuniao: ["Reunião não encontrada", "esta reunião", "a reunião"],
  peca: ["Peça não encontrada", "esta peça", "a peça"],
  pessoa: ["Pessoa não encontrada", "esta pessoa", "a pessoa"],
  fluxo: ["Fluxo não encontrado", "este fluxo", "o fluxo"],
  contratos: ["Modelo não encontrado", "este modelo", "o modelo"],
};

/* Por que um registro some sem ninguém avisar. Neutro de propósito: serve para
   os cinco alvos sem concordar com nenhum. */
const POR_QUE_SOME =
  "Isso acontece quando o registro foi apagado ou arquivado, ou quando ele é de outro escritório — o link continua o mesmo, o registro é que saiu do alcance.";

/**
 * @param falha  `{ status, mensagem, daCasa }` — o erro que `api.js` levantou.
 *               `status: 0` é "não chegou resposta" (rede fora, prazo estourado).
 * @param chave  qual registro não abriu: `atendimento`, `reuniao`, `peca`,
 *               `pessoa` ou `fluxo`.
 * @returns `{ titulo, texto, botao }`, com `botao` em `voltar | tentar | entrar`
 *          — qual é a saída ÓBVIA daqui, não a única.
 */
export function ausencia(falha, chave = "atendimento") {
  const [titulo404, esteAlvo, oAlvo] = ALVOS[chave] ?? ALVOS.atendimento;
  const status = Number(falha?.status ?? 0);
  const mensagem = falha?.mensagem ?? "";
  /* Frase de emergência da tela não vale como recado do servidor. */
  const doServidor = falha?.daCasa && mensagem ? mensagem : "";

  if (status === 404 || status === 410)
    return {
      titulo: titulo404,
      texto: `${doServidor || `Não há ${oAlvo} deste endereço aqui.`} ${POR_QUE_SOME}`,
      botao: "voltar",
    };

  if (status === 401)
    return {
      titulo: "Sua sessão terminou",
      texto:
        "Você foi desconectado — pode ter sido o tempo parado, uma senha trocada ou o acesso desativado. Entre de novo para continuar de onde parou.",
      botao: "entrar",
    };

  if (status === 403)
    return {
      titulo: `Sem acesso a ${esteAlvo}`,
      texto: `${doServidor || "Seu acesso não alcança este registro."} Pode ser de outro escritório, ou de uma área que o seu papel não inclui. Quem cuida dos acessos, na tela de Equipe, consegue liberar.`,
      botao: "voltar",
    };

  /* Endereço malformado: a rota casou, o id não é número, e ninguém chegou a
     pedir nada. Tentar de novo repete o mesmo endereço errado — a saída é sair. */
  if (status === 400)
    return {
      titulo: "Endereço incompleto",
      texto: `${doServidor || mensagem || `O endereço não diz qual é ${oAlvo}.`} Abra pela lista, e o endereço certo aparece na barra do navegador.`,
      botao: "voltar",
    };

  if (status === 0)
    return {
      titulo: "O servidor não respondeu",
      texto: `${mensagem || "Não deu para falar com o servidor."} O que estava na tela continua valendo; nada foi perdido.`,
      botao: "tentar",
    };

  return {
    titulo: `Não deu para abrir ${oAlvo}`,
    texto: `${mensagem || `O servidor recusou (${status}).`} Tente de novo; se continuar, chame quem cuida do servidor.`,
    botao: "tentar",
  };
}
